/**
 * WI-B-06 — the A7 session-stale banner + the sign-out confirm modal, ported
 * 1:1 from the reference DOM. The banner is the ONLY symptom of an expired
 * session: it mounts as the first child of app-main, above screen content, on
 * every tab, and reads the WI-B-05 session-stale signal.
 */

import { useEffect, useState } from "preact/hooks";
import { isSessionStale, onSessionStaleChange } from "../auth/session-stale";
import { PillButton } from "./PillButton";
import { Modal } from "./Modal";

const AMBER = "var(--mark-accent-amber)";
const WARN_TINT = "var(--mark-bg-warning-tint)";
const TEXT = "var(--mark-text-primary)";
const MUTED = "var(--mark-text-muted)";
const BORDER = "var(--mark-border-default)";

/** Subscribe a component to the session-stale signal. */
export function useSessionStale(): boolean {
  const [stale, setStale] = useState<boolean>(isSessionStale());
  useEffect(() => onSessionStaleChange(setStale), []);
  return stale;
}

/**
 * A7 — session-stale banner. Renders nothing when the session is healthy; when
 * stale, pins a non-dismissible amber banner that taps through to re-auth (A1
 * "Sign back in"). Capture keeps working behind it — this is a signal, not a
 * gate (PIN-B-4).
 */
export function SessionStaleBanner({
  queuedCount,
  onSignIn,
  stale: staleProp,
}: {
  queuedCount: number;
  onSignIn: () => void;
  /** When supplied, the parent owns the stale subscription and drives visibility
   * (used by AuthedShell, which already subscribes). When omitted, the banner
   * self-gates via its own useSessionStale() hook. */
  stale?: boolean;
}) {
  const selfStale = useSessionStale();
  const stale = staleProp ?? selfStale;
  if (!stale) return null;
  const n = queuedCount;
  return (
    <button
      type="button"
      data-testid="session-stale-banner"
      onClick={onSignIn}
      style={{ flex: "none", display: "flex", alignItems: "center", gap: "12px", width: "100%", textAlign: "left", border: "none", borderRadius: "12px", background: WARN_TINT, boxShadow: `inset 0 0 0 1.5px ${AMBER}`, padding: "14px 16px", cursor: "pointer" }}
    >
      <span style={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
        <span style={{ fontSize: "13.5px", fontWeight: 700, color: TEXT }}>Signed out &mdash; nothing is lost</span>
        <span style={{ fontSize: "12px", lineHeight: 1.45, color: MUTED }}>
          {n > 0
            ? `${n} ${n === 1 ? "capture is" : "captures are"} waiting to send. Keep working — nothing sends until you sign in.`
            : "Keep working — you’ll need to sign in again to send."}
        </span>
      </span>
      <span style={{ flex: "none", fontSize: "12px", fontWeight: 700, color: AMBER }}>SIGN IN</span>
    </button>
  );
}

/**
 * Sign-out confirm modal — type-OUT-to-confirm. The dismiss handler lives on the
 * backdrop; the inner card MUST stopPropagation (reference note: else tapping the
 * input closes the modal). Sign-out PRESERVES the outbox — the caller never
 * flushes it; this component only collects the confirmation.
 */
export function SignOutModal({
  queuedCount,
  onConfirm,
  onCancel,
}: {
  queuedCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState("");
  const armed = value.trim().toUpperCase() === "OUT";
  const n = queuedCount;
  return (
    <Modal
      testid="signout-modal"
      zIndex={50}
      scrim={75}
      padding="24px"
      onClose={onCancel}
      card={{ testid: "signout-card", width: "100%", maxWidth: "345px", padding: "24px", gap: "14px", shadow: true }}
    >
      <span style={{ fontSize: "17px", fontWeight: 700, color: TEXT }}>Sign out of Mark?</span>
      {n > 0 && (
        <div style={{ borderRadius: "12px", background: WARN_TINT, boxShadow: `inset 0 0 0 1.5px ${AMBER}`, padding: "14px 16px" }}>
          <p style={{ margin: 0, fontSize: "13px", lineHeight: 1.5, color: TEXT }}>
            {n} {n === 1 ? "capture hasn’t" : "captures haven’t"} sent yet. They stay saved on this phone and send after you sign back in &mdash; nothing is deleted.
          </p>
        </div>
      )}
      <p style={{ margin: 0, fontSize: "12.5px", lineHeight: 1.5, color: MUTED }}>
        Type <span style={{ color: TEXT, fontWeight: 700 }}>OUT</span> to confirm.
      </p>
      <input
        type="text"
        data-testid="signout-confirm-input"
        value={value}
        onInput={(e) => setValue((e.currentTarget as HTMLInputElement).value)}
        placeholder="OUT"
        style={{ width: "100%", boxSizing: "border-box", background: "transparent", border: `1px solid ${BORDER}`, borderRadius: "14px", color: TEXT, fontFamily: "inherit", fontSize: "13px", padding: "12px 16px", minHeight: "44px" }}
      />
      <PillButton
        size="md"
        tone="critical"
        fullWidth
        data-testid="signout-confirm"
        disabled={!armed}
        onClick={() => armed && onConfirm()}
      >
        Sign out
      </PillButton>
      <button
        type="button"
        data-testid="signout-cancel"
        onClick={onCancel}
        style={{ width: "100%", height: "44px", border: "none", borderRadius: "22px", background: "transparent", boxShadow: `inset 0 0 0 1px ${BORDER}`, color: MUTED, fontFamily: "inherit", fontSize: "13.5px", fontWeight: 600, cursor: "pointer" }}
      >
        Cancel
      </button>
    </Modal>
  );
}
